import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/lib/auth";
import { Link } from "wouter";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { 
  Plus, 
  Edit, 
  Trash2, 
  ArrowLeft, 
  Home, 
  Target,
  Users,
  Calendar,
  DollarSign,
  Heart,
  MapPin,
  Music,
  Sparkles
} from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { apiRequest } from "@/lib/queryClient";
import { MaxSpinner } from "@/components/ui/max-spinner";
import Navbar from "@/components/navbar";
import SearchableMultiSelect from "@/components/ui/searchable-multi-select";
import EmojiPicker from "@/components/ui/emoji-picker";

// Schema for quick persona forms
const quickPersonaSchema = z.object({
  name: z.string().min(1, "Name is required"),
  icon: z.string().min(1, "Icon is required"),
  description: z.string().optional(),
  ageRanges: z.array(z.string()).default([]),
  genders: z.array(z.string()).default([]),
  ethnicities: z.array(z.string()).default([]),
  incomeLevels: z.array(z.string()).default([]),
  targetLocations: z.array(z.string()).default([]),
  musicGenres: z.array(z.string()).default([]),
  lifestyleAttributes: z.array(z.string()).default([]),
  isActive: z.boolean().default(true),
  sortOrder: z.number().default(0),
});

type QuickPersonaForm = z.infer<typeof quickPersonaSchema>;

interface QuickPersona {
  id: number;
  name: string;
  icon: string;
  description: string | null;
  ageRanges: string[];
  genders: string[];
  ethnicities: string[];
  incomeLevels: string[];
  targetLocations: string[];
  musicGenres: string[];
  lifestyleAttributes: string[];
  isActive: boolean;
  sortOrder: number;
  createdAt: string;
  updatedAt: string;
}

export default function AdminQuickPersonas() {
  const { user } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [editingPersona, setEditingPersona] = useState<QuickPersona | null>(null);

  // Check admin access
  if (!user || (user.role !== 'admin' && user.role !== 'super_admin')) {
    return (
      <div className="min-h-screen bg-neutral-50">
        <Navbar />
        <div className="max-w-4xl mx-auto px-4 py-16 pt-24">
          <Card>
            <CardContent className="text-center py-8">
              <p className="text-lg text-neutral-600">Access denied. Admin privileges required.</p>
              <Link to="/">
                <Button className="mt-4">
                  <Home className="w-4 h-4 mr-2" />
                  Go Home
                </Button>
              </Link>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // Fetch quick personas
  const { data: personas = [], isLoading } = useQuery({
    queryKey: ['/api/quick-personas'],
    queryFn: async () => {
      const response = await apiRequest('/api/quick-personas');
      return response; // apiRequest already returns parsed JSON
    }
  });

  // Fetch filter options for form
  const { data: filterOptions = {} } = useQuery({
    queryKey: ['/api/filter-options-grouped'],
    queryFn: async () => {
      const response = await fetch('/api/filter-options');
      if (!response.ok) return {};
      
      const options = await response.json();
      const grouped: Record<string, any[]> = {};
      options.forEach((option: any) => {
        if (!grouped[option.category]) {
          grouped[option.category] = [];
        }
        if (option.isActive) {
          grouped[option.category].push({
            value: option.value,
            label: option.label,
            description: option.description,
          });
        }
      });
      
      return grouped;
    },
  });

  // Fetch dynamic data
  const { data: dynamicGenres = [] } = useQuery({
    queryKey: ['/api/filter-options', 'musicGenres'],
    queryFn: async () => {
      const response = await fetch('/api/filter-options?category=musicGenres');
      if (!response.ok) return [];
      return response.json();
    },
  });

  const { data: dynamicEthnicities = [] } = useQuery({
    queryKey: ['/api/filter-options', 'fanEthnicity'],
    queryFn: async () => {
      const response = await fetch('/api/filter-options?category=fanEthnicity');
      if (!response.ok) return [];
      return response.json();
    },
  });

  const { data: dynamicAttributes = [] } = useQuery({
    queryKey: ['/api/filter-options', 'fanAttributes'],
    queryFn: async () => {
      const response = await fetch('/api/filter-options?category=fanAttributes');
      if (!response.ok) return [];
      return response.json();
    },
  });

  const { data: dmaOptions = [] } = useQuery({
    queryKey: ['/api/dmas'],
    queryFn: async () => {
      const response = await fetch('/api/dmas');
      if (!response.ok) return [];
      const dmas = await response.json();
      return dmas.map((dma: any) => ({
        value: dma.name,
        label: dma.name,
        description: `Rank #${dma.rank} - ${dma.states?.join(', ') || ''}`
      }));
    },
  });

  // Form setup
  const form = useForm<QuickPersonaForm>({
    resolver: zodResolver(quickPersonaSchema),
    defaultValues: {
      name: "",
      icon: "🎯",
      description: "",
      ageRanges: [],
      genders: [],
      ethnicities: [],
      incomeLevels: [],
      targetLocations: [],
      musicGenres: [],
      lifestyleAttributes: [],
      isActive: true,
      sortOrder: 0,
    },
  });

  // Create mutation
  const createPersonaMutation = useMutation({
    mutationFn: async (data: QuickPersonaForm) => {
      const response = await apiRequest('/api/quick-personas', {
        method: 'POST',
        body: JSON.stringify(data),
      });
      if (!response.ok) {
        throw new Error('Failed to create persona');
      }
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/quick-personas'] });
      toast({ title: "Success", description: "Quick persona created successfully" });
      setShowCreateDialog(false);
      form.reset();
    },
    onError: (error) => {
      toast({ 
        title: "Error", 
        description: error instanceof Error ? error.message : "Failed to create persona",
        variant: "destructive" 
      });
    },
  });

  // Update mutation
  const updatePersonaMutation = useMutation({
    mutationFn: async ({ id, data }: { id: number; data: QuickPersonaForm }) => {
      try {
        console.log('Updating persona with data:', { id, data });
        const response = await apiRequest(`/api/quick-personas/${id}`, {
          method: 'PUT',
          body: data, // Remove JSON.stringify since apiRequest handles it
        });
        console.log('Update response:', response);
        return response;
      } catch (error) {
        console.error('Update error:', error);
        throw error;
      }
    },
    onSuccess: (data) => {
      console.log('Update succeeded:', data);
      queryClient.invalidateQueries({ queryKey: ['/api/quick-personas'] });
      toast({ title: "Success", description: "Quick persona updated successfully" });
      setEditingPersona(null);
      setShowCreateDialog(false);
      form.reset();
    },
    onError: (error) => {
      console.error('Update mutation error:', error);
      toast({ 
        title: "Error", 
        description: error instanceof Error ? error.message : "Failed to update persona",
        variant: "destructive" 
      });
    },
  });

  // Delete mutation
  const deletePersonaMutation = useMutation({
    mutationFn: async (id: number) => {
      const response = await apiRequest(`/api/quick-personas/${id}`, {
        method: 'DELETE',
      });
      if (!response.ok) {
        throw new Error('Failed to delete persona');
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/quick-personas'] });
      toast({ title: "Success", description: "Quick persona deleted successfully" });
    },
    onError: (error) => {
      toast({ 
        title: "Error", 
        description: error instanceof Error ? error.message : "Failed to delete persona",
        variant: "destructive" 
      });
    },
  });

  const handleEditPersona = (persona: QuickPersona) => {
    setEditingPersona(persona);
    form.reset({
      name: persona.name,
      icon: persona.icon,
      description: persona.description || "",
      ageRanges: persona.ageRanges || [],
      genders: persona.genders || [],
      ethnicities: persona.ethnicities || [],
      incomeLevels: persona.incomeLevels || [],
      targetLocations: persona.targetLocations || [],
      musicGenres: persona.musicGenres || [],
      lifestyleAttributes: persona.lifestyleAttributes || [],
      isActive: persona.isActive,
      sortOrder: persona.sortOrder,
    });
    setShowCreateDialog(true);
  };

  const onSubmit = (data: QuickPersonaForm) => {
    if (editingPersona) {
      updatePersonaMutation.mutate({ id: editingPersona.id, data });
    } else {
      createPersonaMutation.mutate(data);
    }
  };

  const handleDeletePersona = (persona: QuickPersona) => {
    if (confirm(`Are you sure you want to delete "${persona.name}"? This action cannot be undone.`)) {
      deletePersonaMutation.mutate(persona.id);
    }
  };

  const handleCloseDialog = () => {
    setShowCreateDialog(false);
    setEditingPersona(null);
    form.reset();
  };

  return (
    <div className="min-h-screen bg-neutral-50">
      <Navbar />
      
      <div className="max-w-7xl mx-auto px-4 py-16 pt-24">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <Link to="/admin">
              <Button variant="outline" size="sm">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Admin
              </Button>
            </Link>
            <div>
              <h1 className="text-3xl font-bold text-neutral-900">Quick Personas Management</h1>
              <p className="text-lg text-neutral-600 mt-2">
                Manage quick persona templates available to all users
              </p>
            </div>
          </div>

          <Button onClick={() => setShowCreateDialog(true)}>
            <Plus className="w-4 h-4 mr-2" />
            Create Quick Persona
          </Button>
        </div>

        {/* Quick Personas Grid */}
        {isLoading ? (
          <div className="flex justify-center py-8">
            <MaxSpinner />
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {personas.map((persona: any) => (
              <Card key={persona.id} className="hover:shadow-md transition-shadow flex flex-col h-full">
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="text-2xl">{persona.icon}</div>
                      <div>
                        <CardTitle className="text-lg">{persona.name}</CardTitle>
                        <Badge variant={persona.isActive ? "default" : "secondary"} className="mt-1">
                          {persona.isActive ? "Active" : "Inactive"}
                        </Badge>
                      </div>
                    </div>
                    <div className="text-xs text-neutral-500">
                      Order: {persona.sortOrder}
                    </div>
                  </div>
                </CardHeader>
                
                <CardContent className="flex flex-col flex-1">
                  <p className="text-sm text-neutral-600 mb-4 line-clamp-3 h-[3.6rem] overflow-hidden">
                    {persona.description || "This persona template helps users quickly target their ideal audience with pre-configured demographic and lifestyle preferences."}
                  </p>

                  {/* Attributes Summary - Two Columns */}
                  <div className="grid grid-cols-2 gap-x-4 gap-y-2 mb-4 flex-1">
                    <div className="flex items-center gap-2">
                      <Calendar className="w-3 h-3 text-blue-500" />
                      <span className="text-xs text-neutral-600">
                        {persona.ageRanges?.length || 0} age range{(persona.ageRanges?.length || 0) !== 1 ? 's' : ''}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Users className="w-3 h-3 text-purple-500" />
                      <span className="text-xs text-neutral-600">
                        {persona.genders?.length || 0} gender{(persona.genders?.length || 0) !== 1 ? 's' : ''}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Users className="w-3 h-3 text-orange-500" />
                      <span className="text-xs text-neutral-600">
                        {persona.ethnicities?.length || 0} ethnicit{(persona.ethnicities?.length || 0) !== 1 ? 'ies' : 'y'}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <DollarSign className="w-3 h-3 text-green-500" />
                      <span className="text-xs text-neutral-600">
                        {persona.incomeLevels?.length || 0} income level{(persona.incomeLevels?.length || 0) !== 1 ? 's' : ''}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <MapPin className="w-3 h-3 text-red-500" />
                      <span className="text-xs text-neutral-600">
                        {persona.targetLocations?.length || 0} location{(persona.targetLocations?.length || 0) !== 1 ? 's' : ''}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Music className="w-3 h-3 text-indigo-500" />
                      <span className="text-xs text-neutral-600">
                        {persona.musicGenres?.length || 0} music genre{(persona.musicGenres?.length || 0) !== 1 ? 's' : ''}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Heart className="w-3 h-3 text-pink-500" />
                      <span className="text-xs text-neutral-600">
                        {persona.lifestyleAttributes?.length || 0} lifestyle attribute{(persona.lifestyleAttributes?.length || 0) !== 1 ? 's' : ''}
                      </span>
                    </div>
                  </div>

                  {/* Actions - pushed to bottom */}
                  <div className="flex justify-end gap-2 mt-auto">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleEditPersona(persona)}
                    >
                      <Edit className="w-3 h-3 mr-1" />
                      Edit
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleDeletePersona(persona)}
                      className="text-red-600 hover:text-red-700"
                    >
                      <Trash2 className="w-3 h-3 mr-1" />
                      Delete
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Create/Edit Dialog */}
        <Dialog open={showCreateDialog} onOpenChange={handleCloseDialog}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Target className="h-5 w-5" />
                {editingPersona ? 'Edit Quick Persona' : 'Create Quick Persona'}
              </DialogTitle>
            </DialogHeader>

            <Form {...form}>
              <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Basic Info */}
                  <FormField
                    control={form.control}
                    name="name"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Name *</FormLabel>
                        <FormControl>
                          <Input placeholder="e.g., Young Professionals" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="icon"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Icon (Emoji) *</FormLabel>
                        <FormControl>
                          <EmojiPicker
                            value={field.value}
                            onSelect={field.onChange}
                            placeholder="Choose an icon for this persona"
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="sortOrder"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Sort Order</FormLabel>
                        <FormControl>
                          <Input 
                            type="number" 
                            placeholder="0" 
                            {...field}
                            onChange={(e) => field.onChange(parseInt(e.target.value) || 0)}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="isActive"
                    render={({ field }) => (
                      <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
                        <div className="space-y-0.5">
                          <FormLabel className="text-base">Active</FormLabel>
                          <div className="text-sm text-muted-foreground">
                            Show this persona to users
                          </div>
                        </div>
                        <FormControl>
                          <input
                            type="checkbox"
                            checked={field.value}
                            onChange={field.onChange}
                            className="w-4 h-4"
                          />
                        </FormControl>
                      </FormItem>
                    )}
                  />
                </div>

                <FormField
                  control={form.control}
                  name="description"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Description</FormLabel>
                      <FormControl>
                        <Textarea 
                          placeholder="Describe this persona..."
                          rows={3}
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {/* Demographics */}
                <div className="space-y-4">
                  <h3 className="text-lg font-medium">Demographics</h3>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* Age Ranges */}
                    <FormField
                      control={form.control}
                      name="ageRanges"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Age Ranges</FormLabel>
                          <SearchableMultiSelect
                            options={filterOptions.fanAge || [
                              { value: '16-20 years', label: '16-20 years' },
                              { value: '21-29 years', label: '21-29 years' },
                              { value: '30-39 years', label: '30-39 years' },
                              { value: '40-49 years', label: '40-49 years' },
                              { value: '50-59 years', label: '50-59 years' },
                              { value: '60+ years', label: '60+ years' },
                            ]}
                            value={field.value}
                            onChange={field.onChange}
                            placeholder="Select age ranges..."
                            icon={<Calendar className="h-4 w-4" />}
                          />
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    {/* Gender */}
                    <FormField
                      control={form.control}
                      name="genders"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Gender</FormLabel>
                          <SearchableMultiSelect
                            options={filterOptions.fanGender || [
                              { value: 'Male', label: 'Male' },
                              { value: 'Female', label: 'Female' },
                              { value: 'Split', label: 'Split' },
                            ]}
                            value={field.value}
                            onChange={field.onChange}
                            placeholder="Select genders..."
                            icon={<Users className="h-4 w-4" />}
                          />
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    {/* Income Levels */}
                    <FormField
                      control={form.control}
                      name="incomeLevels"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Income Levels</FormLabel>
                          <SearchableMultiSelect
                            options={filterOptions.fanIncome || [
                              { value: 'Less than $30K', label: 'Less than $30K' },
                              { value: '$30K-$49K', label: '$30K-$49K' },
                              { value: '$50K-$74K', label: '$50K-$74K' },
                              { value: '$75K-$125K', label: '$75K-$125K' },
                              { value: '$125K or More', label: '$125K or More' },
                            ]}
                            value={field.value}
                            onChange={field.onChange}
                            placeholder="Select income levels..."
                            icon={<DollarSign className="h-4 w-4" />}
                          />
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    {/* Ethnicity */}
                    <FormField
                      control={form.control}
                      name="ethnicities"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Ethnicity</FormLabel>
                          <SearchableMultiSelect
                            options={dynamicEthnicities}
                            value={field.value}
                            onChange={field.onChange}
                            placeholder="Select ethnicities..."
                            icon={<Users className="h-4 w-4" />}
                          />
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                </div>

                {/* Interests & Lifestyle */}
                <div className="space-y-4">
                  <h3 className="text-lg font-medium">Interests & Lifestyle</h3>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* Target Locations */}
                    <FormField
                      control={form.control}
                      name="targetLocations"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Target Markets (DMAs)</FormLabel>
                          <SearchableMultiSelect
                            options={dmaOptions}
                            value={field.value}
                            onChange={field.onChange}
                            placeholder="Select target markets..."
                            icon={<MapPin className="h-4 w-4" />}
                          />
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    {/* Lifestyle Attributes */}
                    <FormField
                      control={form.control}
                      name="lifestyleAttributes"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Lifestyle Attributes</FormLabel>
                          <SearchableMultiSelect
                            options={dynamicAttributes}
                            value={field.value}
                            onChange={field.onChange}
                            placeholder="Select lifestyle attributes..."
                            icon={<Heart className="h-4 w-4" />}
                          />
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    {/* Music Genres */}
                    <div className="md:col-span-2">
                      <FormField
                        control={form.control}
                        name="musicGenres"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Preferred Music Genres</FormLabel>
                            <SearchableMultiSelect
                              options={dynamicGenres}
                              value={field.value}
                              onChange={field.onChange}
                              placeholder="Select music genres..."
                              icon={<Music className="h-4 w-4" />}
                            />
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>
                  </div>
                </div>

                <DialogFooter>
                  <Button type="button" variant="outline" onClick={handleCloseDialog}>
                    Cancel
                  </Button>
                  <Button 
                    type="submit" 
                    disabled={createPersonaMutation.isPending || updatePersonaMutation.isPending}
                  >
                    {createPersonaMutation.isPending || updatePersonaMutation.isPending ? (
                      <MaxSpinner className="w-4 h-4 mr-2" />
                    ) : null}
                    {editingPersona ? 'Update Persona' : 'Create Persona'}
                  </Button>
                </DialogFooter>
              </form>
            </Form>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}